import * as React from 'react'
import {
  RangeCalendar as AriaRangeCalendar,
  CalendarGrid,
  CalendarGridHeader,
  CalendarGridBody,
  CalendarHeaderCell,
  CalendarCell,
  Heading,
  Button as AriaButton,
  type RangeCalendarProps as AriaRangeCalendarProps,
  type DateValue,
} from 'react-aria-components'
import { CaretLeft, CaretRight } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { CalendarMonthYearSelect } from './calendar-month-year-select'

/**
 * RangeCalendar — pick a contiguous start→end date range, built on
 * react-aria-components (keyboard range extension with Shift+arrows, min/max,
 * unavailable dates). Same composed shape and tokens as `Calendar`.
 *
 *   <RangeCalendar value={range} onChange={setRange} />
 *
 * The two endpoints are basil-600 fills (rounded on their outer edge); the days
 * between are a basil-50 band (square, so they read as one connected range).
 * Borderless — wrap in a card or Popover. Boolean props map to react-aria `is*`.
 */

const navButtonClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-fbui-gray-500 outline-none transition-colors data-[hovered]:bg-fbui-gray-100 data-[hovered]:text-fbui-gray-700 data-[focus-visible]:ring-2 data-[focus-visible]:ring-fbui-ring-brand data-[disabled]:text-fbui-gray-300 data-[disabled]:cursor-not-allowed'

// Endpoints (selection-start/end) come after `selected` so their basil-600 fill
// wins over the basil-50 in-range band; the band is square to connect visually.
const cellClass =
  'flex size-9 cursor-pointer items-center justify-center rounded-lg text-sm text-fbui-gray-700 outline-none transition-colors data-[hovered]:bg-fbui-gray-100 data-[focus-visible]:ring-2 data-[focus-visible]:ring-inset data-[focus-visible]:ring-fbui-ring-brand data-[selected]:rounded-none data-[selected]:bg-fbui-basil-50 data-[selected]:text-fbui-basil-700 data-[selection-start]:rounded-l-lg data-[selection-start]:bg-fbui-basil-600 data-[selection-start]:text-fbui-base-white data-[selection-end]:rounded-r-lg data-[selection-end]:bg-fbui-basil-600 data-[selection-end]:text-fbui-base-white data-[disabled]:cursor-default data-[disabled]:text-fbui-gray-300 data-[unavailable]:cursor-default data-[unavailable]:text-fbui-gray-300 data-[unavailable]:line-through data-[outside-month]:text-fbui-gray-300'

export interface RangeCalendarProps extends Omit<
  AriaRangeCalendarProps<DateValue>,
  'isDisabled' | 'isReadOnly' | 'className'
> {
  disabled?: boolean
  readOnly?: boolean
  /** Show month + year dropdowns in the header instead of a static label. */
  monthYearSelect?: boolean
  /** Number of months shown side by side (1 default, 2 for wide range picking). */
  months?: 1 | 2
  className?: string
}

/** One month grid (optionally offset, for the second pane of a two-month view). */
const MonthGrid = ({ offset }: { offset?: { months: number } }) => (
  <CalendarGrid
    weekdayStyle="short"
    className="border-collapse"
    offset={offset}
  >
    <CalendarGridHeader>
      {(day) => (
        <CalendarHeaderCell className="size-9 text-xs font-normal text-fbui-gray-500">
          {day}
        </CalendarHeaderCell>
      )}
    </CalendarGridHeader>
    <CalendarGridBody>
      {(date) => <CalendarCell date={date} className={cellClass} />}
    </CalendarGridBody>
  </CalendarGrid>
)

export const RangeCalendar = React.forwardRef<
  HTMLDivElement,
  RangeCalendarProps
>(
  (
    { disabled, readOnly, monthYearSelect, months = 1, className, ...props },
    ref
  ) => (
    <AriaRangeCalendar
      ref={ref}
      isDisabled={disabled}
      isReadOnly={readOnly}
      visibleDuration={months === 2 ? { months: 2 } : undefined}
      className={cn('w-fit', className)}
      {...props}
    >
      <header className="mb-2 flex items-center justify-between gap-2">
        <AriaButton
          slot="previous"
          aria-label="Previous month"
          className={navButtonClass}
        >
          <CaretLeft weight="bold" className="size-4" aria-hidden="true" />
        </AriaButton>
        {monthYearSelect ? (
          <CalendarMonthYearSelect />
        ) : (
          <Heading className="text-sm font-semibold text-fbui-text-title" />
        )}
        <AriaButton
          slot="next"
          aria-label="Next month"
          className={navButtonClass}
        >
          <CaretRight weight="bold" className="size-4" aria-hidden="true" />
        </AriaButton>
      </header>
      {months === 2 ? (
        <div className="flex gap-6">
          <MonthGrid />
          <MonthGrid offset={{ months: 1 }} />
        </div>
      ) : (
        <MonthGrid />
      )}
    </AriaRangeCalendar>
  )
)
RangeCalendar.displayName = 'RangeCalendar'
